"use client"

import type { DefaultError, QueryKey } from "@tanstack/react-query"
import type { Omit } from "@wagmi/core/internal"
import { zeroAddress } from "viem"
import { useAccount, useChainId, useConfig, useConnectorClient } from "wagmi"
import type { SimulateEVCBatchCallErrorType } from "../actions/simulateEVCBatchCall"
import type { TransactionItems } from "../factories"
import {
  type SimulateEVCBatchCallData,
  type SimulateEVCBatchCallOptions,
  type SimulateEVCBatchCallQueryFnData,
  type SimulateEVCBatchCallQueryKey,
  simulateEVCBatchCallQueryOptions,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
  UseQueryReturnType,
} from "../types"
import { useQuery } from "../utils"

type QueryParameter<
  queryFnData = unknown,
  error = DefaultError,
  data = queryFnData,
  queryKey extends QueryKey = QueryKey,
> = {
  query?:
    | Omit<
        UseQueryParameters<queryFnData, error, data, queryKey>,
        "queryFn" | "queryHash" | "queryKey" | "queryKeyHashFn" | "throwOnError"
      >
    | undefined
}

export type UseSimulateEVCBatchCallParameters<
  config extends ExtendedConfig = ExtendedConfig,
  transactions extends TransactionItems = TransactionItems,
  chainId extends config["chains"][number]["id"] | undefined = undefined,
  selectData = SimulateEVCBatchCallData<config, chainId>,
> = SimulateEVCBatchCallOptions<config, transactions, chainId> &
  ConfigParameter<config> &
  QueryParameter<
    SimulateEVCBatchCallQueryFnData<config, chainId>,
    SimulateEVCBatchCallErrorType,
    selectData,
    SimulateEVCBatchCallQueryKey<config, transactions, chainId>
  >

export type UseSimulateEVCBatchCallReturnType<
  config extends ExtendedConfig = ExtendedConfig,
  chainId extends config["chains"][number]["id"] | undefined = undefined,
  selectData = SimulateEVCBatchCallData<config, chainId>,
> = UseQueryReturnType<selectData, SimulateEVCBatchCallErrorType>

export function useSimulateEVCBatchCall<
  config extends ExtendedConfig = ExtendedConfig,
  transactions extends TransactionItems = TransactionItems,
  chainId extends config["chains"][number]["id"] | undefined = undefined,
  selectData = SimulateEVCBatchCallData<config, chainId>,
>(
  parameters: UseSimulateEVCBatchCallParameters<
    config,
    transactions,
    chainId,
    selectData
  > = {} as any,
): UseSimulateEVCBatchCallReturnType<config, chainId, selectData> {
  const { transactions, connector, query = {} } = parameters
  // Get config first to pass to other hooks - avoids WagmiProviderNotFoundError in production
  const config = useConfig(parameters)
  const { address } = useAccount({ config })
  const { data: connectorClient } = useConnectorClient({
    config,
    connector,
    query: { enabled: parameters.account === undefined },
  })
  const chainId = useChainId({ config })
  const account = parameters.account ?? address ?? connectorClient?.account
  const isZero =
    (typeof account === "string" ? account : account?.address) === zeroAddress
  const options = simulateEVCBatchCallQueryOptions<
    config,
    transactions,
    chainId
  >(config, {
    ...parameters,
    account,
    chainId: parameters.chainId ?? chainId,
  })
  const enabled = Boolean(
    transactions?.length && (query.enabled ?? true) && account && !isZero,
  )

  return useQuery({
    ...query,
    ...options,
    enabled,
    refetchOnMount: false,
    retry: false,
  })
}
